import {
  BoundRadioGroupField,
  BoundSelectField,
  Button,
  Css,
  FormLines,
  ModalBody,
  ModalFooter,
  ModalHeader,
  RadioFieldOption,
  useComputed,
  useModal,
  useSnackbar,
  useTestIds,
} from "@homebound/beam";
import { ListFieldState, ObjectConfig, ObjectState, required, useFormState } from "@homebound/form-state";
import { runInAction } from "mobx";
import { Observer } from "mobx-react";
import { IncrementalCollectionOp, InputMaybe, PlanMilestoneTargetInput } from "src/generated/graphqlTypes";
import {
  useCopyMilestoneTargetsModal_CommunitiesQuery,
  useCopyMilestoneTargetsModal_SourceScheduleLazyQuery,
} from "src/routes/projects/settings/CopyMilestoneTargetsModal.generated";
import { ScheduleTab_PlanMilestoneTargetFragment } from "src/routes/projects/settings/ScheduleTab.generated";
import { isDefined } from "src/utils";

type CopyMilestoneTargetsMode = "replace" | "append";

type MilestoneTargetFormRow = PlanMilestoneTargetInput & { globalPlanMilestoneName?: InputMaybe<string> };

type CopyMilestoneTargetsModalProps = {
  projectId: string;
  milestoneTargetsField: ListFieldState<MilestoneTargetFormRow>;
};

export function CopyMilestoneTargetsModal(props: CopyMilestoneTargetsModalProps) {
  const { projectId, milestoneTargetsField } = props;
  const { closeModal } = useModal();
  const { triggerNotice } = useSnackbar();
  const tid = useTestIds({}, "copyMilestoneTargetsModal");

  const formState = useFormState({ config: formConfig, init: { input: { mode: "replace", sourceCommunityId: null } } });

  const communitiesQuery = useCopyMilestoneTargetsModal_CommunitiesQuery();
  // Once a source community is selected, we lazy fetch the milestone targets upon clicking "copy"
  // rather than requesting the full list of communities and their targets in a single query.
  const [loadSourceSchedule, { loading: loadingSource }] = useCopyMilestoneTargetsModal_SourceScheduleLazyQuery();

  // Remove the current community from the list of communities to copy from.
  // Currently do not have any other filter criteria for copyable communities to move to a backend filter.
  const communityOptions =
    communitiesQuery.data?.communities.entities.filter((community) => community.id !== projectId) ?? [];

  async function onCopy() {
    const sourceCommunityId = formState.sourceCommunityId.value;
    if (!sourceCommunityId) return;

    const { data } = await loadSourceSchedule({ variables: { sourceCommunityId } });
    const sourceTargets = data?.planSchedule.planMilestoneTargets ?? [];

    const copied = applyCopiedMilestoneTargets(milestoneTargetsField, sourceTargets, formState.mode.value ?? "replace");

    if (!copied) {
      triggerNotice({ message: "The selected community has no milestone targets to copy.", icon: "error" });
      return;
    }

    closeModal();
  }

  const canCopy = useComputed(
    () => formState.valid && !!formState.sourceCommunityId.value && !loadingSource,
    [formState, loadingSource],
  );

  return (
    <>
      <ModalHeader>Copy Milestone Targets</ModalHeader>
      <ModalBody>
        <p css={Css.mb3.gray700.$}>Choose another community to copy milestone targets from.</p>
        <FormLines width="full">
          <BoundSelectField
            field={formState.sourceCommunityId}
            label="Community"
            options={communityOptions}
            getOptionValue={(community) => community.id}
            getOptionLabel={(community) => community.name}
            disabled={communitiesQuery.loading ? "Loading communities..." : undefined}
            {...tid.community}
          />
          <BoundRadioGroupField
            field={formState.mode}
            label="Copy mode"
            layout="vertical"
            options={copyModeOptions}
            {...tid.mode}
          />
        </FormLines>
      </ModalBody>
      <ModalFooter>
        <Button variant="quaternary" label="Cancel" onClick={closeModal} {...tid.cancel} />
        <Observer>{() => <Button label="Copy" onClick={onCopy} disabled={!canCopy} {...tid.copy} />}</Observer>
      </ModalFooter>
    </>
  );
}

type CopyMilestoneTargetsModalFormValue = {
  sourceCommunityId: InputMaybe<string>;
  mode: InputMaybe<CopyMilestoneTargetsMode>;
};

const copyModeOptions: RadioFieldOption<CopyMilestoneTargetsMode>[] = [
  { value: "replace", label: "Replace existing" },
  { value: "append", label: "Append missing" },
];

const formConfig: ObjectConfig<CopyMilestoneTargetsModalFormValue> = {
  sourceCommunityId: { type: "value", rules: [required] },
  mode: { type: "value", rules: [required] },
};

// Prefill the milestone target form from a source community (does not autosave: user can still cancel changes after this copy)
function applyCopiedMilestoneTargets(
  milestoneTargetsField: ListFieldState<MilestoneTargetFormRow>,
  sourceTargets: ScheduleTab_PlanMilestoneTargetFragment[],
  mode: CopyMilestoneTargetsMode,
): boolean {
  if (sourceTargets.isEmpty) return false;

  // Batch all mobx form-state mutations so we get a single re-render
  runInAction(() => {
    if (mode === "replace") {
      replaceExistingMilestoneTargets(milestoneTargetsField, sourceTargets);
    }
    // Replace reconciles active rows first; addMissing then fills gaps (new milestones or soft-deleted rows).
    addMissingMilestoneTargets(milestoneTargetsField, sourceTargets);
  });

  return true;
}

// Reconcile existing rows: update target days for matches, remove extras, revive already-marked-for-deletion matches
function replaceExistingMilestoneTargets(
  milestoneTargetsField: ListFieldState<MilestoneTargetFormRow>,
  sourceTargets: ScheduleTab_PlanMilestoneTargetFragment[],
) {
  const sourceByMilestoneId = sourceTargets.keyBy((target) => target.globalPlanMilestone.id);

  milestoneTargetsField.rows.forEach((row) => {
    const milestoneId = row.globalPlanMilestoneId.value;

    // Pending empty row (no milestone selected yet) — drop it; replace will add source rows below.
    if (!milestoneId) {
      return removeMilestoneTargetRow(milestoneTargetsField, row);
    }

    const sourceTarget = sourceByMilestoneId[milestoneId];

    if (sourceTarget) {
      // This milestone existed already in the target so we:
      // 1: Update the target days from RTS
      // 2: Include the row via op: Include (this revives already-marked-for-deletion rows)
      return row.set({ op: IncrementalCollectionOp.Include, targetDaysFromRts: sourceTarget.targetDaysFromRts });
    }

    // Milestone exists in target but not in source so we mark for deletion
    if (row.op.value !== IncrementalCollectionOp.Delete) {
      removeMilestoneTargetRow(milestoneTargetsField, row);
    }
  });
}

// Add source milestones that are not currently active in the form.
function addMissingMilestoneTargets(
  milestoneTargetsField: ListFieldState<MilestoneTargetFormRow>,
  sourceTargets: ScheduleTab_PlanMilestoneTargetFragment[],
) {
  const activeMilestoneIds = getActiveMilestoneIds(milestoneTargetsField);

  sourceTargets
    .filter((target) => !activeMilestoneIds.has(target.globalPlanMilestone.id))
    .forEach((target) => {
      const existingRow = milestoneTargetsField.rows.find(
        (row) => row.globalPlanMilestoneId.value === target.globalPlanMilestone.id,
      );

      if (existingRow) {
        // Rows with op: Delete are excluded from activeMilestoneIds above, so they reach this branch
        // even though the form row exists (but is hidden to the user).
        // Revive with Include instead of adding a duplicate.
        existingRow.set({
          op: IncrementalCollectionOp.Include,
          targetDaysFromRts: target.targetDaysFromRts,
          globalPlanMilestoneName: target.globalPlanMilestone.name,
        });
        return;
      }

      milestoneTargetsField.add({
        globalPlanMilestoneId: target.globalPlanMilestone.id,
        globalPlanMilestoneName: target.globalPlanMilestone.name,
        targetDaysFromRts: target.targetDaysFromRts,
        op: IncrementalCollectionOp.Include,
      });
    });
}

// Milestone IDs with a visible (non-deleted) row in the form.
function getActiveMilestoneIds(milestoneTargetsField: ListFieldState<MilestoneTargetFormRow>) {
  return new Set(
    milestoneTargetsField.rows
      .filter((row) => row.op.value !== IncrementalCollectionOp.Delete)
      .map((row) => row.globalPlanMilestoneId.value)
      .filter(isDefined),
  );
}

function removeMilestoneTargetRow(
  milestoneTargetsField: ListFieldState<MilestoneTargetFormRow>,
  row: ObjectState<MilestoneTargetFormRow>,
) {
  if (row.id.originalValue) {
    // Persisted row: mark for deletion so the backend receives a Delete op on save.
    row.op.set(IncrementalCollectionOp.Delete);
  } else {
    // Unsaved row: drop from the list entirely.
    milestoneTargetsField.remove(row.value);
  }
}
